/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { SearchField } from 'components/general/search-field/search-field'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { OrderFilterType, ordersActions, selectOrdersFilters } from './order.slice'
import { Container, SearchFieldContainer, SearchRow } from 'components/general/table/TableFilters'
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { getDateFormat } from 'translation/translation-helper'
import { OrderItemStatus } from './models/order'

// Statuses shown in the reports table (item-level), in the order they read most naturally.
const FILTERABLE_STATUSES: OrderItemStatus[] = ['Pending', 'Processing', 'Completed', 'Failed', 'Rejected']

const styles = {
    filtersRow: css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 24px;
        margin-top: 12px;
    `,
    status: css`
        width: 200px;
    `,
    datePicker: css`
        width: 160px;
    `,
}

type OrdersTableFiltersProps = {
    onSubmit: () => void
}

export const OrdersTableFilters: React.FC<OrdersTableFiltersProps> = ({ onSubmit }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectOrdersFilters)

    // Auto-apply when a discrete filter (status / date range) changes. This runs after the
    // consumer has re-rendered with the new filters, so onSubmit re-queries with fresh params.
    // Search text (q) is intentionally excluded here — it keeps applying on submit as before.
    const didMount = useRef(false)
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true
            return
        }
        onSubmit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.itemStatus, filters.dateFrom, filters.dateTo])

    const onStatusChange = (event: SelectChangeEvent) => {
        dispatch(ordersActions.setFilters({ filter: OrderFilterType.itemStatus, value: event.target.value || undefined }))
    }

    const onDateChange = (filter: OrderFilterType.dateFrom | OrderFilterType.dateTo) => (value: Dayjs | null) => {
        // Make the range inclusive of the picked days: "from" snaps to start-of-day, "to" to end-of-day.
        const iso = value
            ? (filter === OrderFilterType.dateFrom ? value.startOf('day') : value.endOf('day')).toISOString()
            : undefined
        dispatch(ordersActions.setFilters({ filter, value: iso }))
    }

    return (
        <Container>
            <SearchRow>
                <SearchFieldContainer>
                    <SearchField
                        onSubmit={onSubmit}
                        label={t('TEXT_SEARCH_COMPANIES', 'Søk...')}
                        onTextChanged={(value) => {
                            dispatch(ordersActions.setFilters({ filter: OrderFilterType.q, value }))
                        }}
                    />
                </SearchFieldContainer>
            </SearchRow>

            <div css={styles.filtersRow}>
                <div css={styles.status}>
                    <FormControl variant="standard" fullWidth>
                        <InputLabel id="orders-status-filter-label">{t('TEXT_STATUS', 'Status')}</InputLabel>
                        <Select
                            labelId="orders-status-filter-label"
                            value={filters.itemStatus ?? ''}
                            onChange={onStatusChange}
                        >
                            <MenuItem value="">{t('TEXT_ALL', 'Alle')}</MenuItem>
                            {FILTERABLE_STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {t(`ORDER_ITEM_STATUS_${status}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div css={styles.datePicker}>
                        <DatePicker
                            label={t('TEXT_FROM', 'Fra')}
                            format={getDateFormat()}
                            value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
                            onChange={onDateChange(OrderFilterType.dateFrom)}
                            slotProps={{ textField: { variant: 'standard', fullWidth: true } }}
                        />
                    </div>
                    <div css={styles.datePicker}>
                        <DatePicker
                            label={t('TEXT_TO', 'Til')}
                            format={getDateFormat()}
                            value={filters.dateTo ? dayjs(filters.dateTo) : null}
                            onChange={onDateChange(OrderFilterType.dateTo)}
                            slotProps={{ textField: { variant: 'standard', fullWidth: true } }}
                        />
                    </div>
                </LocalizationProvider>
            </div>
        </Container>
    )
}
